import { FeatureFlags } from '@dupe/common/constants'
import { getCookie, setCookie } from 'cookies-next'
import { useRouter } from 'next/compat/router'
import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  isBrowser,
  isChrome,
  isDesktop,
  isMobile,
  isMobileSafari,
} from 'react-device-detect'
import { useAppContext } from '~/src/context/AppProvider'
import useExtensionState from '~/src/hooks/useExtensionState'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import useIsSupportedCountries from '~/src/hooks/useIsSupportedCountries'

// TODO: move to constants
const SUPPORTED_COUNTRIES = ['us', 'ca']
const DUPE_EXTENSION_MODAL_HASHES_COOKIE_KEY = '__dupe_ext_modal_hashes'
const DUPE_EXTENSION_MODAL_SHOWN_COOKIE_KEY = '__dupe_ext_modal_shown'

interface IExtensionModalContext {
  loading: boolean
  featureOn: boolean | null
  installed?: boolean
  shouldBlockContent: boolean
  blockContentFeatureOn: boolean | null
  shouldShowModal: boolean
  modalAlreadyShown: boolean
  setModalShown: () => void
  showHellobar: boolean
  setShowHellobar: (show: boolean) => void
  safariMobile: boolean
}

const defaultContext = {
  loading: true,
  featureOn: false,
  installed: false,
  shouldBlockContent: false,
  blockContentFeatureOn: false,
  shouldShowModal: false,
  modalAlreadyShown: false,
  setModalShown: () => {},
  showHellobar: false,
  setShowHellobar: () => {},
  safariMobile: false,
}

// Create the context
const ExtensionModalContext =
  createContext<IExtensionModalContext>(defaultContext)

// Custom hook to access the context
export const useExtensionModalProvider = () => {
  const context = useContext(ExtensionModalContext)
  if (!context) {
    throw new Error(
      'useExtensionModalProvider must be used within a <ExtensionModalProvider> component'
    )
  }
  return context
}

const set24HourCookie = (key: string, value: any) => {
  const expires = new Date()
  expires.setTime(expires.getTime() + 24 * 60 * 60 * 1000) // 24 hours
  setCookie(key, value, {
    expires,
    path: '/',
  })
}

// Provider component
const ExtensionModalProvider: React.FC<
  React.PropsWithChildren<{ disableModal?: boolean }>
> = ({ children, disableModal }) => {
  const { hash } = useAppContext()
  // next/compat/router: null when this always-mounted provider renders under
  // the app router (raw next/router crashes the /_not-found and /reports
  // prerenders with "NextRouter was not mounted"). The extension sidepanel
  // only loads pages-router routes (/extension/*, /[hash]/sidepanel), so under
  // app/ the pre-DUP-716 behavior (effectiveInstalled = installed) is correct.
  const router = useRouter()
  const { loading, installed } = useExtensionState()
  const isExtensionRoute =
    router != null &&
    (router.pathname.startsWith('/extension') ||
      router.pathname.includes('/sidepanel') ||
      router.query.sidepanel != null)
  // ⚠️ `&&` is load-bearing here, more than in the auth-modal case: this feeds
  // `effectiveInstalled`, so accepting the route alone would treat any web
  // visitor who lands on a `/sidepanel` URL as having the extension installed —
  // hiding install prompts and the extension wall from someone who does not.
  // `ext=1` is the extension's own assertion; `middleware.ts` now forwards it.
  const isExtensionSidepanel = router?.query.ext === '1' && isExtensionRoute
  const effectiveInstalled = installed || isExtensionSidepanel
  const [showHellobar, setShowHellobar] = useState(false)
  const [total, setTotal] = useState(0)

  const { isInSupportedCountry, isLoading } =
    useIsSupportedCountries(SUPPORTED_COUNTRIES)

  const { featureOn, loading: loadingFeatureFlag } = useIsFeatureOn(
    FeatureFlags.DesktopExtensionWall
  )

  const { featureOn: blockContentFeatureOn } = useIsFeatureOn(
    FeatureFlags.DesktopExtensionBlockContent
  )

  const blockContentFeatureOnMobileSafari = false
  // const { featureOn: blockContentFeatureOnMobileSafari } = useIsFeatureOn(
  //   FeatureFlags.DesktopExtensionBlockContentMobileSafari
  // )

  // const isBrave = browserName === 'Brave'
  // const isEdge = browserName === 'Edge'

  const modalAlreadyShown = !!getCookie(DUPE_EXTENSION_MODAL_SHOWN_COOKIE_KEY)
  const shouldShowModal =
    !disableModal && !!featureOn && !modalAlreadyShown && total > 1

  const blockSupportedCountries = isInSupportedCountry && !isLoading

  const shouldBlockMobileSafari =
    isMobileSafari &&
    isMobile &&
    blockContentFeatureOnMobileSafari &&
    !loading &&
    !effectiveInstalled &&
    blockSupportedCountries // Ensure the user is in a supported country (iOS only supports US and CA at the moment)

  const shouldBlockContentDesktop =
    isBrowser &&
    isChrome &&
    !!blockContentFeatureOn &&
    !loading &&
    !effectiveInstalled &&
    isDesktop

  const shouldBlockContent =
    shouldBlockMobileSafari || shouldBlockContentDesktop

  const setModalShown = () => {
    set24HourCookie(DUPE_EXTENSION_MODAL_SHOWN_COOKIE_KEY, '1')
  }

  useEffect(() => {
    if (!hash) return
    if (loadingFeatureFlag) return
    if (!featureOn) return

    const existingCookie = getCookie(DUPE_EXTENSION_MODAL_HASHES_COOKIE_KEY)
    const existingCookieArray =
      (typeof existingCookie === 'string' ? existingCookie : '')?.split(',') ||
      []
    const value = Array.from(new Set([...existingCookieArray, hash]))
    const valueStr = value.join(',')

    setTotal(value.length)
    set24HourCookie(DUPE_EXTENSION_MODAL_HASHES_COOKIE_KEY, valueStr)
  }, [hash, featureOn, loadingFeatureFlag])

  return (
    <ExtensionModalContext.Provider
      value={{
        featureOn: !!featureOn,
        loading,
        installed,
        shouldBlockContent,
        blockContentFeatureOn: !!blockContentFeatureOn,
        shouldShowModal,
        modalAlreadyShown,
        setModalShown,
        showHellobar,
        setShowHellobar,
        safariMobile: shouldBlockMobileSafari || false,
      }}
    >
      {children}
    </ExtensionModalContext.Provider>
  )
}

export default ExtensionModalProvider
